<script setup>
import axios from '@axios'
import moment from 'moment'
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useDisplay } from 'vuetify'
import {
  changedVersions,
  groupByCapability,
  hasProblems,
  historyWording,
  insertFillIn,
  isEdited,
  isOverWarning,
  lengthLine,
  segmentsLabel,
  segmentWarningLine,
  sortHistory,
} from '@/utils/agentReplies'

/*
 * RM-349 — the RM AI replies table: every fixed reply RM writes itself (not
 * RM AI's own answers), with its App & email version and its Text version.
 * An edit is used within a minute (each server process refreshes every minute),
 * with no deploy; a reply nobody has
 * edited uses the code's wording (lib/agent/replyCatalog.js).
 *
 * The server owns the wording rules: the preview endpoint fills the sample
 * values, measures the text version (characters, segments, cost) and reports
 * anything that would stop it saving. This page only edits and shows.
 */

const VERSIONS = [
  { id: 'app', label: 'App & email' },
  { id: 'text', label: 'Text' },
]

// ── the list ────────────────────────────────────────────────────────────────
const loading = ref(false)
const loadError = ref('')
const replies = ref([])
const flows = ref([])
const capabilities = ref([])
const textReplyTargetChars = ref(450)
const segmentWarning = ref(3)
const search = ref('')

// D17: grouped by capability, then flow
const groups = computed(() => groupByCapability(replies.value, { capabilities: capabilities.value, flows: flows.value, query: search.value }))

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await axios.get('/agent/admin/replies')
    replies.value = res.data.replies || []
    flows.value = res.data.flows || []
    capabilities.value = res.data.capabilities || []
    if (Number.isFinite(res.data.textReplyTargetChars)) textReplyTargetChars.value = res.data.textReplyTargetChars
    if (Number.isFinite(res.data.segmentWarning)) segmentWarning.value = res.data.segmentWarning
  } catch (e) {
    loadError.value = e.response?.data?.error || 'Could not load the replies.'
  } finally {
    loading.value = false
  }
}

// ── one reply, open ─────────────────────────────────────────────────────────
const { width } = useDisplay()
const phone = computed(() => width.value <= 600)

const open = ref(false)
const selected = ref(null) // the reply as last loaded or saved
const draft = ref({ app: '', text: '' })
const saving = ref(false)
const resetting = ref(false)
const confirmReset = ref(false)
const dialogError = ref('')

const dirty = computed(() => !!selected.value
  && (draft.value.app !== selected.value.app.current || draft.value.text !== selected.value.text.current))

function applyReply(reply) {
  if (!reply) return
  replies.value = replies.value.map(r => (r.key === reply.key ? reply : r))
  if (selected.value?.key === reply.key) selected.value = reply
}

// Live preview on the sample values — debounced, and only the newest answer counts.
const preview = ref(null) // { app, text, textLength, problems }
const previewing = ref(false)
const previewError = ref('')
let previewSeq = 0
let requestedFor = '' // the draft the newest preview request was for
let previewPromise = Promise.resolve()

const previewId = () => JSON.stringify([selected.value?.key, draft.value.app, draft.value.text])

/** Preview the draft; resolves once the preview shown is for it (Save waits on this). */
function runPreview() {
  if (!selected.value || !open.value) return Promise.resolve()
  const forId = previewId()
  if (forId === requestedFor) return previewPromise
  requestedFor = forId
  const seq = ++previewSeq
  previewing.value = true
  previewError.value = ''
  previewPromise = axios.post('/agent/admin/replies/preview', {
    key: selected.value.key,
    app: draft.value.app,
    text: draft.value.text,
  })
    .then(res => {
      if (seq === previewSeq) preview.value = res.data
    })
    .catch(e => {
      if (seq !== previewSeq) return
      requestedFor = ''
      previewError.value = e.response?.data?.error || 'Could not update the preview.'
    })
    .finally(() => {
      if (seq === previewSeq) previewing.value = false
    })

  return previewPromise
}
const schedulePreview = useDebounceFn(runPreview, 300)
watch(draft, () => { if (open.value) schedulePreview() }, { deep: true })

const problems = computed(() => preview.value?.problems || { app: [], text: [] })
const textLength = computed(() => preview.value?.textLength || selected.value?.text?.length || null)
const textWarning = computed(() => segmentWarningLine(textLength.value, segmentWarning.value))
// A reply RM only ever sends by text (RM via Text's own messages) shows its Text version alone,
// and one RM only ever sends by email (the follow-up email, RM-359) its App & email version alone.
const shownVersions = computed(() => {
  if (selected.value?.textOnly)
    return VERSIONS.filter(v => v.id === 'text')
  if (selected.value?.emailOnly)
    return VERSIONS.filter(v => v.id === 'app')

  return VERSIONS
})
const canSave = computed(() => dirty.value && !selected.value?.locked && !saving.value && !resetting.value && !hasProblems(problems.value))

// History, loaded when first opened.
const emptyHistory = () => ({ open: false, loading: false, loaded: false, versions: [], error: '' })
const history = ref(emptyHistory())

async function loadHistory() {
  if (!selected.value) return
  history.value.loading = true
  history.value.error = ''
  try {
    const res = await axios.get(`/agent/admin/replies/${encodeURIComponent(selected.value.key)}/history`)
    history.value.versions = sortHistory(res.data.versions)
    history.value.loaded = true
  } catch (e) {
    history.value.error = e.response?.data?.error || 'Could not load the history.'
  } finally {
    history.value.loading = false
  }
}

function toggleHistory() {
  history.value.open = !history.value.open
  if (history.value.open && !history.value.loaded && !history.value.loading) loadHistory()
}

// Fill-in chips put {name} at the cursor (or the end, before the field is used).
const fields = {}
const focused = { app: false, text: false }
const token = name => `{${name}}`

function setField(id, component) {
  fields[id] = component
}

function onFocus(id) {
  focused[id] = true
}

async function insert(id, name) {
  const el = fields[id]?.$el?.querySelector('textarea') || null
  const useCursor = el && focused[id]
  const { value, caret } = insertFillIn(draft.value[id], name, useCursor ? el.selectionStart : undefined, useCursor ? el.selectionEnd : undefined)
  draft.value = { ...draft.value, [id]: value }
  await nextTick()
  if (el) {
    el.focus()
    el.setSelectionRange(caret, caret)
  }
}

// ── feedback ────────────────────────────────────────────────────────────────
const snackbar = ref({ show: false, message: '' })

function notify(message) {
  snackbar.value = { show: true, message }
}

const when = d => (d ? moment(d).format('MMM D, YYYY h:mm a') : '')

// ── open, save, reset ───────────────────────────────────────────────────────
function openReply(reply) {
  previewSeq++
  requestedFor = ''
  selected.value = reply
  draft.value = { app: reply.app.current, text: reply.text.current }
  preview.value = null
  previewError.value = ''
  dialogError.value = ''
  history.value = emptyHistory()
  focused.app = false
  focused.text = false
  open.value = true
  runPreview()
}

function close() {
  open.value = false
}

watch(open, isOpen => {
  if (isOpen) return
  previewSeq++ // a late preview answer no longer counts
  requestedFor = ''
  previewing.value = false
})

async function save() {
  if (!selected.value || !dirty.value) return
  saving.value = true
  dialogError.value = ''
  try {
    // Make sure the problems shown are for what is about to be saved.
    await runPreview()
    if (hasProblems(problems.value)) return
    const body = changedVersions(selected.value, draft.value)
    const res = await axios.put(`/agent/admin/replies/${encodeURIComponent(selected.value.key)}`, body)
    applyReply(res.data.reply)
    open.value = false
    notify('Saved - RM uses it within a minute')
  } catch (e) {
    dialogError.value = e.response?.data?.error || 'Could not save this reply.'
  } finally {
    saving.value = false
  }
}

async function reset() {
  if (!selected.value) return
  resetting.value = true
  dialogError.value = ''
  try {
    const res = await axios.post(`/agent/admin/replies/${encodeURIComponent(selected.value.key)}/reset`)
    const reply = res.data.reply
    applyReply(reply)
    if (reply) draft.value = { app: reply.app.current, text: reply.text.current }
    history.value.loaded = false
    if (history.value.open) loadHistory()
    notify('Back to the default wording')
  } catch (e) {
    dialogError.value = e.response?.data?.error || 'Could not reset this reply.'
  } finally {
    resetting.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="replies-admin">
    <div class="replies-admin__head">
      <h1 class="replies-admin__title">
        RM AI replies
      </h1>
      <p class="replies-admin__lede">
        The fixed replies RM writes itself, word for word, each with an App &amp; email version and a Text version. RM AI's own answers aren't here; by text those aim for about {{ textReplyTargetChars }} characters. An edit reaches RM's replies within a minute, with no deploy. A reply nobody has edited uses the code's wording.
      </p>
    </div>

    <div class="replies-admin__controls">
      <VTextField
        v-model="search"
        placeholder="Search name, key, or wording"
        variant="outlined"
        density="comfortable"
        clearable
        hide-details
        class="replies-admin__search"
      />
    </div>

    <VAlert
      v-if="loadError"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ loadError }}
      <VBtn
        variant="text"
        size="small"
        class="ml-2"
        :loading="loading"
        @click="load"
      >
        Try again
      </VBtn>
    </VAlert>

    <div
      v-if="loading && !replies.length"
      class="replies-admin__loading"
      aria-busy="true"
    >
      <VSkeletonLoader
        v-for="n in 6"
        :key="n"
        type="list-item-two-line"
      />
    </div>

    <div
      v-else-if="!groups.length && !loadError"
      class="replies-admin__empty"
    >
      <p class="replies-admin__empty-title">
        {{ search ? 'No replies match.' : 'No replies yet.' }}
      </p>
      <p
        v-if="search"
        class="replies-admin__empty-body"
      >
        Search looks at each reply's name, its key and the wording of both versions.
      </p>
    </div>

    <section
      v-for="cap in groups"
      :key="cap.capability"
      class="replies-admin__group"
    >
      <h2 class="replies-admin__capability">
        {{ cap.capability }}
      </h2>
      <div
        v-for="g in cap.flows"
        :key="g.flow"
        class="replies-admin__flowgroup"
      >
        <h3
          v-if="g.heading"
          class="replies-admin__flow"
        >
          {{ g.flow }}
        </h3>
        <ul class="replies-admin__list">
          <li
            v-for="r in g.replies"
            :key="r.key"
          >
            <button
              type="button"
              class="replies-admin__row"
              @click="openReply(r)"
            >
              <span class="replies-admin__row-main">
                <span class="replies-admin__where">{{ r.where }}</span>
                <span class="replies-admin__key">{{ r.key }}</span>
              </span>
              <span class="replies-admin__row-meta">
                <VChip
                  v-if="isEdited(r)"
                  size="x-small"
                  color="primary"
                  variant="tonal"
                >
                  Edited
                </VChip>
                <!-- A text's length; an email-only reply (RM-359) has none -->
                <VChip
                  v-if="!r.emailOnly && isOverWarning(r.text.length, segmentWarning)"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                >
                  {{ segmentsLabel(r.text.length.segments) }}
                </VChip>
                <span
                  v-else-if="!r.emailOnly && r.text.length"
                  class="replies-admin__meta"
                >{{ segmentsLabel(r.text.length.segments) }}</span>
              </span>
              <VIcon
                icon="mdi-chevron-right"
                size="20"
                class="replies-admin__caret"
              />
            </button>
          </li>
        </ul>
      </div>
    </section>

    <!-- One reply: both versions, their previews, and its history -->
    <VDialog
      v-model="open"
      :fullscreen="phone"
      :max-width="phone ? undefined : 960"
      :persistent="dirty"
      scrollable
    >
      <VCard
        v-if="selected"
        class="reply-edit"
      >
        <div class="reply-edit__head">
          <div class="reply-edit__head-text">
            <h2 class="reply-edit__title">
              {{ selected.where }}
            </h2>
            <div class="reply-edit__key">
              {{ selected.key }}
            </div>
          </div>
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            aria-label="Close"
            @click="close"
          />
        </div>
        <VDivider />

        <VCardText class="reply-edit__body">
          <p
            v-if="selected.emailUses === 'text' && !selected.textOnly"
            class="reply-edit__note"
          >
            Email sends the Text version of this reply today.
          </p>
          <!-- RM-349: a reply registered with the texting campaign is shown, not edited -->
          <p
            v-if="selected.locked"
            class="reply-edit__note"
            data-test="reply-locked"
          >
            Read only. {{ selected.locked }}
          </p>

          <div class="reply-edit__versions">
            <section
              v-for="v in shownVersions"
              :key="v.id"
              class="reply-edit__version"
            >
              <div class="reply-edit__label-row">
                <h3 class="reply-edit__label">
                  {{ v.label }}
                </h3>
                <VChip
                  v-if="selected[v.id].edited"
                  size="x-small"
                  color="primary"
                  variant="tonal"
                >
                  Edited
                </VChip>
              </div>

              <VTextarea
                :ref="c => setField(v.id, c)"
                v-model="draft[v.id]"
                variant="outlined"
                rows="3"
                auto-grow
                hide-details="auto"
                :error-messages="problems[v.id]"
                :readonly="!!selected.locked"
                :aria-label="`${v.label} wording`"
                class="reply-edit__field"
                @focus="onFocus(v.id)"
              />

              <div
                v-if="selected.fillIns.length"
                class="reply-edit__fillins"
              >
                <span class="replies-admin__meta">Fill-ins</span>
                <VChip
                  v-for="n in selected.fillIns"
                  :key="n"
                  size="small"
                  variant="outlined"
                  label
                  :aria-label="`Insert ${token(n)}`"
                  @click="insert(v.id, n)"
                >
                  {{ token(n) }}
                </VChip>
              </div>

              <!-- pre-wrap: the wording's own line breaks show, so no whitespace around it -->
              <p
                v-if="draft[v.id] !== selected[v.id].default"
                class="reply-edit__default"
              >
                {{ `Default: ${selected[v.id].default}` }}
              </p>

              <div class="reply-edit__preview-label">
                Preview with sample values
              </div>
              <div
                v-if="preview"
                class="reply-edit__preview"
                :class="{ 'reply-edit__preview--busy': previewing }"
              >
                {{ preview[v.id] }}
              </div>
              <div
                v-else
                class="reply-edit__preview reply-edit__preview--empty"
              >
                {{ previewError || 'Loading preview…' }}
              </div>

              <template v-if="v.id === 'text'">
                <p class="reply-edit__length">
                  {{ lengthLine(textLength) }}
                </p>
                <VAlert
                  v-if="textWarning"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  {{ textWarning }}
                </VAlert>
              </template>
            </section>
          </div>

          <p
            v-if="preview && previewError"
            class="reply-edit__error-line"
          >
            {{ previewError }}
          </p>

          <div class="reply-edit__history">
            <VBtn
              variant="text"
              size="small"
              class="px-1"
              :append-icon="history.open ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              :aria-expanded="history.open ? 'true' : 'false'"
              @click="toggleHistory"
            >
              History
            </VBtn>
            <div
              v-if="history.open"
              class="reply-edit__history-body"
            >
              <VProgressLinear
                v-if="history.loading"
                indeterminate
                color="primary"
              />
              <p
                v-else-if="history.error"
                class="reply-edit__error-line"
              >
                {{ history.error }}
              </p>
              <p
                v-else-if="!history.versions.length"
                class="replies-admin__meta"
              >
                No edits yet - this reply uses the code's wording.
              </p>
              <ol
                v-else
                class="reply-edit__history-list"
              >
                <li
                  v-for="h in history.versions"
                  :key="h._id || h.version"
                  class="reply-edit__history-item"
                >
                  <div class="replies-admin__meta">
                    Version {{ h.version }} · {{ h.updatedBy || 'Unknown' }} · {{ when(h.createdAt) }}
                  </div>
                  <div
                    v-for="v in shownVersions"
                    :key="v.id"
                    class="reply-edit__history-line"
                  >
                    <span class="reply-edit__history-label">{{ v.label }}</span>
                    <span :class="{ 'replies-admin__meta': h[v.id] == null }">{{ historyWording(h[v.id]) }}</span>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </VCardText>

        <VAlert
          v-if="dialogError"
          type="error"
          variant="tonal"
          density="compact"
          class="reply-edit__alert"
        >
          {{ dialogError }}
        </VAlert>

        <VDivider />
        <VCardActions class="reply-edit__actions">
          <VBtn
            v-if="isEdited(selected)"
            variant="text"
            color="secondary"
            :loading="resetting"
            :disabled="saving"
            @click="confirmReset = true"
          >
            Reset to default
          </VBtn>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="saving"
            @click="close"
          >
            {{ selected.locked ? 'Close' : 'Cancel' }}
          </VBtn>
          <VBtn
            v-if="!selected.locked"
            color="primary"
            variant="flat"
            :loading="saving"
            :disabled="!canSave"
            @click="save"
          >
            Save
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model:is-dialog-visible="confirmReset"
      confirmation-msg="Put both versions of this reply back to the code's wording? The edits stay in its history."
      confirm-btn-text="Reset to default"
      @confirm="reset"
    />

    <VSnackbar
      v-model="snackbar.show"
      timeout="3000"
    >
      {{ snackbar.message }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.replies-admin {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}

.replies-admin__head {
  margin-bottom: 24px;
}

.replies-admin__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 8px;
}

.replies-admin__lede {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin: 0;
  max-width: 640px;
}

.replies-admin__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 8px;
}

.replies-admin__search {
  flex: 1 1 280px;
  max-width: 420px;
}

/* iOS zooms any input under 16px — keep the search at the body size. */
.replies-admin__search :deep(input) {
  font-size: 16px;
}

.replies-admin__meta {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.replies-admin__group {
  margin-top: 24px;
}

.replies-admin__capability {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
}

.replies-admin__flowgroup + .replies-admin__flowgroup {
  margin-top: 16px;
}

.replies-admin__flow {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 0 0 4px;
}

.replies-admin__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.replies-admin__list > li + li {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* The whole row opens the reply; it bleeds 8px so the hover sits off the text. */
.replies-admin__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas: 'main meta caret';
  align-items: center;
  gap: 4px 12px;
  width: calc(100% + 16px);
  margin: 0 -8px;
  padding: 12px 8px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.replies-admin__row:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.replies-admin__row:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

.replies-admin__row-main {
  grid-area: main;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.replies-admin__where {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.replies-admin__key {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  overflow-wrap: anywhere;
}

.replies-admin__row-meta {
  grid-area: meta;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.replies-admin__caret {
  grid-area: caret;
  color: rgba(var(--v-theme-on-surface), 0.4);
}

.replies-admin__empty {
  padding: 40px 16px;
  text-align: center;
}

.replies-admin__empty-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
}

.replies-admin__empty-body {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.7);
  max-width: 480px;
  margin: 0 auto;
}

.replies-admin__loading {
  margin-top: 24px;
}

/* ── the open reply ─────────────────────────────────────────────────────── */

.reply-edit__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 12px 24px;
}

.reply-edit__head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.reply-edit__title {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 4px;
  overflow-wrap: anywhere;
}

.reply-edit__key {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.reply-edit__body {
  padding: 16px 24px 24px !important;
}

.reply-edit__note {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin: 0 0 16px;
}

.reply-edit__versions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px 32px;
}

.reply-edit__version {
  min-width: 0;
}

.reply-edit__label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.reply-edit__label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
}

/* iOS zooms any input under 16px. */
.reply-edit__field :deep(textarea) {
  font-size: 16px;
  line-height: 1.5;
}

.reply-edit__fillins {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.reply-edit__default {
  font-size: 12px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 8px 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.reply-edit__preview-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 16px 0 4px;
}

.reply-edit__preview {
  font-size: 14px;
  line-height: 1.5;
  padding: 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  min-height: 48px;
  transition: opacity 150ms ease;
}

.reply-edit__preview--busy {
  opacity: 0.6;
}

.reply-edit__preview--empty {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.reply-edit__length {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 8px 0 0;
}

.reply-edit__error-line {
  font-size: 12px;
  line-height: 1.4;
  color: rgb(var(--v-theme-error));
  margin: 8px 0 0;
}

.reply-edit__history {
  margin-top: 24px;
  padding-top: 12px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.reply-edit__history-body {
  margin-top: 8px;
}

.reply-edit__history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reply-edit__history-line {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 4px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.reply-edit__history-label {
  font-size: 12px;
  line-height: 1.75;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: normal;
}

.reply-edit__alert {
  margin: 0 24px 12px;
  flex: 0 0 auto;
}

.reply-edit__actions {
  padding: 12px 16px !important;
  gap: 8px;
}

@media (max-width: 600px) {
  .replies-admin__row {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'main caret'
      'meta caret';
  }

  .replies-admin__row-meta {
    justify-content: flex-start;
  }

  .reply-edit__head {
    padding: 12px 8px 8px 16px;
  }

  .reply-edit__title {
    font-size: 16px;
  }

  .reply-edit__body {
    padding: 16px !important;
  }

  .reply-edit__versions {
    grid-template-columns: minmax(0, 1fr);
  }

  .reply-edit__alert {
    margin: 0 16px 12px;
  }

  .reply-edit__history-line {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # Platform-admin-only: edits the wording RM sends members. The API enforces
  # requireAdmin() on every /agent/admin/replies endpoint; gating the route
  # stops non-admins from ever loading the page.
  requiresAdmin: true
</route>
